import React, { useState, useEffect } from "react"
import { manageAccountEstateCreate } from "../../../../../api/manage_account_estate_create"
import { getManageEstateList } from "../../../../../api/manage_estate_list"
import { useNavigate } from "react-router-dom"
import { ManageEstateData } from "../../../estate/"
import { useLoading } from "../../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { Checkbox, FormControlLabel } from "@mui/material"

interface ManageEstateCreateProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
}

const ManageAccountEstateCreate: React.FC<ManageEstateCreateProps> = ({ onClose }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [catvId, setCatvId] = useState<string>("")
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [formData, setFormData] = useState({
    estate_id: "",
    name: "",
    mail: "",
    auth_facility: false,
    auth_list: false,
    auth_setting: false
  })

  const [errors, setErrors] = useState({
    estate_id: "",
    name: "",
    mail: "",
    auth_facility: "",
    auth_list: "",
    auth_setting: "",
    server: ""
  })

  useEffect(() => {
    const fetchManageEstateList = async () => {
      try {
        startLoading()
        const manageEstateData = await getManageEstateList()
        setEstateData(manageEstateData.estate_list)
        if (manageEstateData.estate_list.length > 0) {
          setCatvId(manageEstateData.estate_list[0].catv_id)
        }
        return manageEstateData.estate_list
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = { ...errors }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = { ...errors }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
    fetchManageEstateList()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    // 絵文字を除外するための正規表現
    const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

    // 必須項目のバリデーション
    if (!formData.estate_id || formData.estate_id == "") {
      validationErrors.estate_id = "必須項目が入力されていません"
      hasError = true
    } else {
      validationErrors.estate_id = ""
    }

    if (!formData.name) {
      validationErrors.name = "必須項目が入力されていません"
      hasError = true
    } else if (formData.name.length > 12) {
      validationErrors.name = "入力文字数は12文字以内です"
      hasError = true
    } else if (emojiRegex.test(formData.name)) {
      validationErrors.name = "絵文字は使用できません"
      hasError = true
    } else {
      validationErrors.name = ""
    }

    if (!formData.mail) {
      validationErrors.mail = "必須項目が入力されていません"
      hasError = true
    } else if (formData.mail.length > 64) {
      validationErrors.mail = "入力文字数は64文字以内です"
      hasError = true
    } else if (emojiRegex.test(formData.mail)) {
      validationErrors.mail = "絵文字は使用できません"
      hasError = true
    } else {
      validationErrors.mail = ""
    }

    validationErrors.server = ""

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return {
      ...data,
      auth_account: data.auth_account ? 1 : 0,
      auth_list: data.auth_list ? 1 : 0,
      auth_setting: data.auth_setting ? 1 : 0,
      auth_logo: data.auth_logo ? 1 : 0
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageAccountEstateCreate(
          catvId,
          convertedData.estate_id,
          convertedData.name,
          convertedData.mail,
          convertedData.auth_facility,
          convertedData.auth_list,
          convertedData.auth_setting
        )
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response && (error.response.status === 403 || error.response.status === 404)) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            estate_id: "",
            name: "",
            mail: "",
            auth_facility: "",
            auth_list: "",
            auth_setting: "",
            server: ""
          }
          validationErrors.mail = errorDetail.message
          setErrors(validationErrors)
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            estate_id: "",
            name: "",
            mail: "",
            auth_facility: "",
            auth_list: "",
            auth_setting: "",
            server: ""
          }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = {
            estate_id: "",
            name: "",
            mail: "",
            auth_facility: "",
            auth_list: "",
            auth_setting: "",
            server: ""
          }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            不動産業者アカウントを追加
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <select
              name="estate_id"
              value={formData.estate_id}
              className={formData.estate_id == "" ? "placeholder" : ""}
              onChange={handleChange}
            >
              <option key="" value="">
                不動産業者を選択してください
              </option>
              {estateData.map((estate) => (
                <option key={estate.id} value={estate.id}>
                  {estate.name}
                </option>
              ))}
            </select>
          </MDBox>
          {errors.estate_id && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.estate_id}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="名前"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.name && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.name}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="メールアドレス"
              type="text"
              name="mail"
              value={formData.mail}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.mail && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.mail}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <FormControlLabel
              control={<Checkbox name="auth_facility" checked={formData.auth_facility} onChange={handleChange} />}
              label="建物及び入居者情報の登録・編集"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <FormControlLabel
              control={<Checkbox name="auth_list" checked={formData.auth_list} onChange={handleChange} />}
              label="ログインアカウント一覧の閲覧"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <FormControlLabel
              control={<Checkbox name="auth_setting" checked={formData.auth_setting} onChange={handleChange} />}
              label="アカウント権限の編集"
            />
          </MDBox>
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              登録
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageAccountEstateCreate
